import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { platformAPI } from "../services/api";
import { telLink, whatsappLink } from "../utils/contact";

/**
 * What owners and cashiers reported with "Report a problem": their words, the
 * page they were on, and the last error their browser saw. Start from the error
 * rather than asking them for it; mark a report handled once answered.
 */
export default function ProblemReports() {
  const [status, setStatus] = useState("open");
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await platformAPI.listProblemReports(status);
      setRows(data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load reports");
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const [notice, setNotice] = useState("");

  // Handling it emails the owner (and whoever sent it) that it is dealt with;
  // the operator may add a few words. Cancelling the box leaves it open.
  const handle = async (row) => {
    const note = window.prompt("Add a word for the owner? It is sent with the email (optional).", "");
    if (note === null) return;

    try {
      const { data } = await platformAPI.handleProblemReport(row.id, note.trim() || undefined);
      setNotice(
        data.notified > 0
          ? `Marked handled. ${data.notified} email${data.notified > 1 ? "s" : ""} sent.`
          : "Marked handled. No email could be sent (no address, or mail is off).",
      );
    } catch (err) {
      setError(err.response?.data?.error || "Could not update this report");
    }
    await load();
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Problem reports</h1>
      <p className="text-sm text-gray-500 mb-6">
        Sent from the dashboard by owners and staff. Each carries the page, language, app version and the last error.
        Questions that come up twice belong in <code>SUPPORT_FAQ.md</code> and then in the help.
      </p>

      <div className="flex gap-2 mb-4">
        {["open", "handled"].map((key) => (
          <button
            key={key}
            onClick={() => setStatus(key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium capitalize ${
              status === key ? "bg-primary-600 text-white" : "bg-white text-gray-600 shadow-sm"
            }`}
          >
            {key}
          </button>
        ))}
      </div>

      {notice && <p className="text-green-700 bg-green-50 rounded-lg px-3 py-2 mb-4 text-sm">{notice}</p>}
      {error && <p className="text-red-600 mb-4">{error}</p>}

      {rows.length === 0 ? (
        <p className="bg-white rounded-xl shadow px-4 py-10 text-center text-gray-400">Nothing here.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((row) => (
            <li key={row.id} className="bg-white rounded-xl shadow p-4 space-y-2">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-gray-900 whitespace-pre-wrap">{row.message}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    <Link to={`/shops/${row.shop_id}`} className="text-primary-700 hover:underline">
                      {row.shop_name}
                    </Link>{" "}
                    · {row.user_name || row.user_email} · {new Date(row.created_at).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-400" dir="ltr">
                    {row.page} · {row.language} · v{row.app_version}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {row.shop_phone && (
                    <>
                      <a href={telLink(row.shop_phone)} className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-sm">
                        📞
                      </a>
                      <a
                        href={whatsappLink(row.shop_phone)}
                        target="_blank"
                        rel="noreferrer"
                        className="px-3 py-1.5 rounded-lg bg-green-100 text-green-800 text-sm"
                      >
                        💬
                      </a>
                    </>
                  )}
                  {row.status === "open" && (
                    <button
                      onClick={() => handle(row)}
                      className="px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium"
                    >
                      Mark handled
                    </button>
                  )}
                </div>
              </div>
              {row.last_error && (
                <pre className="text-xs bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap" dir="ltr">
                  {row.last_error}
                </pre>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
